import { Layout as RaLayout, AppBar, UserMenu, Logout, useGetIdentity, useLocaleState, useTranslate } from 'react-admin';
import { Typography, Avatar, CircularProgress, Backdrop } from '@mui/material';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import TranslateIcon from '@mui/icons-material/Translate';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { jwtDecode } from 'jwt-decode';
import { SUPPORTED_LANGUAGES } from './i18n/languages';
import { AdminPanelWordmark, ADMINPANEL_PRIMARY, ADMINPANEL_PRIMARY_DARK, GENL_DARK } from './components/AdminPanelBrand';
import ConsentGate from './components/ConsentGate';

const SSO_URL = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';

const HELP_URL = 'https://hilfe.generationl-onlineacademy.de/';
const HELP_LOCALES = ['en', 'fr', 'it', 'es', 'nl', 'pl'];

const CustomAppBar = () => {
    const [offcanvasOpen, setOffcanvasOpen] = useState(false);
    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
    const [loadingLanguage, setLoadingLanguage] = useState(false);
    const [locale, setLocale] = useLocaleState();
    const helpUrl = `${HELP_URL}${HELP_LOCALES.includes(locale) ? `${locale}/` : ''}administrator/`;
    const navigate = useNavigate();
    const location = useLocation();
    const { data: identity, isLoading } = useGetIdentity();
    const translate = useTranslate();

    // Statische admin:panel-Farben in die CSS-Custom-Properties, die die
    // Bootstrap-Overrides in azubi-web-styles.css konsumieren. Der Admin ist
    // produktuebergreifend und traegt die admin:panel-Zwischenmarke der
    // generation:l-Familie (seit 2026-09), kein Mandanten-Branding.
    useEffect(() => {
        document.documentElement.style.setProperty('--nav-secondary-color', ADMINPANEL_PRIMARY);
        document.documentElement.style.setProperty('--branding-primary', ADMINPANEL_PRIMARY);
        document.documentElement.style.setProperty('--branding-primary-hover', ADMINPANEL_PRIMARY_DARK);
    }, []);

    // Drawer + Bottom-Bar bleiben dunkel (Site-Palette), die Top-Bar ist hell
    // — analog zum generation:l-Design des SSO-Dashboards.
    const navBgColor = GENL_DARK;

    // Get user data from identity (fetched from SSO userinfo endpoint)
    const firstName = (identity as any)?.firstName || '';
    const lastName = (identity as any)?.lastName || '';
    const email = (identity as any)?.email || identity?.fullName || 'user@example.com';
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || email.charAt(0).toUpperCase() || 'U';
    const avatarUrl = (identity as any)?.avatar || undefined;

    const userData = { firstName, lastName, email, initials, avatarUrl };

    // Get user roles from JWT token
    const getUserRoles = (): string[] => {
        const token = localStorage.getItem('token');
        if (!token) return [];
        try {
            const decoded: any = jwtDecode(token);
            const roles = decoded.roles || [];
            // Issue #267: roles kann als Object {"0":...,"2":...} kommen
            // (vor SSO-Fix). Object.values normalisiert zum Array.
            if (Array.isArray(roles)) return roles;
            if (roles && typeof roles === 'object') return Object.values(roles) as string[];
            return [];
        } catch {
            return [];
        }
    };

    const userRoles = getUserRoles();
    const hasRole = (role: string) => userRoles.includes(role);
    const isOperator = hasRole('ROLE_OPERATOR');
    const isPlatformAdmin = hasRole('ROLE_PLATFORM_ADMIN');
    const isCompanyAdmin = hasRole('ROLE_COMPANY_ADMIN');
    const isContentManager = hasRole('ROLE_CONTENT_MANAGER');
    const isAdmin = isCompanyAdmin || isOperator || isPlatformAdmin;

    const toggleOffcanvas = () => {
        setOffcanvasOpen(!offcanvasOpen);
    };

    const handleNavigate = (path: string) => {
        navigate(path);
        setOffcanvasOpen(false);
    };

    const handleLanguageChange = async (newLocale: string) => {
        if (newLocale !== locale) {
            setLoadingLanguage(true);
            setLocale(newLocale);
            localStorage.setItem('locale', newLocale);
            // Wait for translations to load - give it more time for DeepL API
            await new Promise(resolve => setTimeout(resolve, 2000));
            setLoadingLanguage(false);
        }
        setLanguageMenuOpen(false);
    };

    const currentLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === locale);

    const isPlatform = isOperator || isPlatformAdmin;
    const bottomNavItems = [
        { to: '/', icon: <DashboardIcon />, label: translate('admin.bottomNav.dashboard'), match: (p: string) => p === '/', visible: true },
        { to: '/companies', icon: <BusinessIcon />, label: translate('admin.bottomNav.companies'), match: (p: string) => p.startsWith('/companies'), visible: isPlatform },
        { to: '/users', icon: <PeopleIcon />, label: translate('admin.bottomNav.users'), match: (p: string) => p.startsWith('/users'), visible: isAdmin },
        { to: '/roles', icon: <SecurityIcon />, label: translate('admin.bottomNav.roles'), match: (p: string) => p.startsWith('/roles'), visible: isPlatform },
        { to: '/permissions', icon: <LockIcon />, label: translate('admin.bottomNav.permissions'), match: (p: string) => p.startsWith('/permissions'), visible: isPlatform },
        { to: '/products', icon: <ShoppingCartIcon />, label: translate('admin.bottomNav.products'), match: (p: string) => p.startsWith('/products'), visible: isPlatform },
        { to: '/licenses', icon: <CardMembershipIcon />, label: translate('admin.bottomNav.licenses'), match: (p: string) => p.startsWith('/licenses'), visible: isAdmin },
    ].filter((i) => i.visible);

    return (
        <>
            {/* Bootstrap Navbar */}
            <nav className="navbar navbar-expand-lg fixed-top" style={{ zIndex: 1030, backgroundColor: '#ffffff', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                <div className="container-fluid">
                    {/* Logo/Brand */}
                    <button
                        className="navbar-brand p-0 border-0 bg-transparent"
                        onClick={() => handleNavigate('/')}
                        style={{ cursor: 'pointer' }}
                    >
                        <AdminPanelWordmark height={40} />
                    </button>

                    {/* Right side: Avatar, Logout, Burger Menu.
                        Ticket #36: bei schmaler Ansicht verschwinden Avatar und
                        Abmelden-Button (d-none d-sm-*) statt umzubrechen —
                        Abmelden bleibt ueber das Burger-Menue erreichbar. */}
                    <div className="d-flex align-items-center ms-auto">
                        <div className="me-4 d-none d-sm-block">
                            <Avatar src={userData.avatarUrl} sx={{ width: 40, height: 40, bgcolor: '#c8ced8', color: '#1f3240' }}>
                                <span style={{ fontSize: '16px' }}>{userData.initials}</span>
                            </Avatar>
                        </div>
                        <button
                            onClick={() => {
                                localStorage.removeItem('token');
                                localStorage.removeItem('redirect_after_login');
                                localStorage.removeItem('code_verifier');
                                // Don't set user_logged_out flag - allows auto-login from SSO
                                sessionStorage.clear();
                                const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
                                const dashboardUrl = `${ssoUrl}/dashboard`;
                                window.location.href = dashboardUrl;
                            }}
                            className="btn btn-secondary me-4 d-none d-md-inline-block"
                            style={{
                                whiteSpace: 'nowrap',
                                borderRadius: '290486px',
                                paddingTop: '10.494px',
                                paddingBottom: '10.494px',
                                paddingLeft: '26.1px',
                                paddingRight: '26.1px',
                                width: '165px'
                            }}
                        >
                            <span>{translate('admin.menu.logout')}</span>
                            <i className="bi bi-box-arrow-right ms-2"></i>
                        </button>
                        <button
                            className="btn btn-link p-0"
                            type="button"
                            onClick={toggleOffcanvas}
                            style={{ fontSize: '1.5rem', textDecoration: 'none', color: GENL_DARK }}
                        >
                            <i className="bi bi-list"></i>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Bootstrap Offcanvas Menu (Right Side) */}
            <div className={`offcanvas offcanvas-end ${offcanvasOpen ? 'show' : ''}`}
                 tabIndex={-1}
                 style={{
                     visibility: offcanvasOpen ? 'visible' : 'hidden',
                     width: '550px',
                     backgroundColor: navBgColor
                 }}>
                <div className="offcanvas-header border-bottom" style={{ backgroundColor: navBgColor }}>
                    <div className="d-flex align-items-center w-100">
                        <Avatar src={userData.avatarUrl} sx={{ width: 48, height: 48, bgcolor: '#c8ced8', mr: 2, color: '#1f3240' }}>
                            <span>{userData.initials}</span>
                        </Avatar>
                        <div className="flex-grow-1">
                            <h6 className="mb-0 text-white">
                                {userData.firstName && userData.lastName
                                    ? `${userData.firstName} ${userData.lastName}`
                                    : userData.email}
                            </h6>
                            {userData.firstName && userData.lastName && (
                                <small className="text-white opacity-75">{userData.email}</small>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn-close btn-close-white"
                        onClick={toggleOffcanvas}
                        aria-label="Close"
                    ></button>
                </div>
                
                <div className="offcanvas-body p-0">
                    {/* Navigation Menu */}
                    <div className="list-group list-group-flush">
                        <button
                            onClick={() => handleNavigate("/")}
                            className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname === "/" ? "active" : ""}`}
                            style={{ backgroundColor: "transparent" }}
                        >
                            <DashboardIcon className="me-3" />
                            {translate('admin.menu.dashboard')}
                        </button>
                        {(isOperator || isPlatformAdmin) && (
                            <button
                                onClick={() => handleNavigate('/companies')}
                                className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname.startsWith('/companies') ? 'active' : ''}`}
                                style={{ backgroundColor: 'transparent' }}
                            >
                                <BusinessIcon className="me-3" />
                                {translate('admin.menu.companies')}
                            </button>
                        )}
                        {isAdmin && (
                            <button
                                onClick={() => handleNavigate('/users')}
                                className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname.startsWith('/users') ? 'active' : ''}`}
                                style={{ backgroundColor: 'transparent' }}
                            >
                                <PeopleIcon className="me-3" />
                                {translate('admin.menu.users')}
                            </button>
                        )}
                        {/* Einladungen + Branding leben seit 2026-09 in der App
                            (azubi:web-Feedback: jede App haendelt das selbst). */}
                        {(isOperator || isPlatformAdmin) && (
                            <button
                                onClick={() => handleNavigate('/roles')}
                                className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname.startsWith('/roles') ? 'active' : ''}`}
                                style={{ backgroundColor: 'transparent' }}
                            >
                                <SecurityIcon className="me-3" />
                                {translate('admin.menu.roles')}
                            </button>
                        )}
                        {(isOperator || isPlatformAdmin) && (
                            <button
                                onClick={() => handleNavigate('/permissions')}
                                className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname.startsWith('/permissions') ? 'active' : ''}`}
                                style={{ backgroundColor: 'transparent' }}
                            >
                                <LockIcon className="me-3" />
                                {translate('admin.menu.permissions')}
                            </button>
                        )}
                        {(isOperator || isPlatformAdmin) && (
                            <>
                                <button
                                    onClick={() => handleNavigate('/products')}
                                    className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname.startsWith('/products') ? 'active' : ''}`}
                                    style={{ backgroundColor: 'transparent' }}
                                >
                                    <ShoppingCartIcon className="me-3" />
                                    {translate('admin.menu.products')}
                                </button>
                                <button
                                    onClick={() => handleNavigate('/licenses')}
                                    className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname.startsWith('/licenses') ? 'active' : ''}`}
                                    style={{ backgroundColor: 'transparent' }}
                                >
                                    <CardMembershipIcon className="me-3" />
                                    {translate('admin.menu.licenses')}
                                </button>
                            </>
                        )}
                        {isAdmin && (
                            <button
                                onClick={() => handleNavigate('/organization-settings')}
                                className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname.startsWith('/organization-settings') ? 'active' : ''}`}
                                style={{ backgroundColor: 'transparent' }}
                            >
                                <SettingsIcon className="me-3" />
                                {translate('admin.menu.organizationSettings')}
                            </button>
                        )}
                        {/* Mandanten-Dokumente: Betreiber lädt hoch, Company-Admin
                            sieht die Dokumente des eigenen Mandanten */}
                        {isAdmin && (
                            <button
                                onClick={() => handleNavigate('/documents')}
                                className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname.startsWith('/documents') ? 'active' : ''}`}
                                style={{ backgroundColor: 'transparent' }}
                            >
                                <FolderIcon className="me-3" />
                                {translate('admin.menu.documents')}
                            </button>
                        )}
                        {(isOperator || isPlatformAdmin) && (
                            <button
                                onClick={() => handleNavigate('/legal-documents')}
                                className={`list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white ${location.pathname.startsWith('/legal-documents') ? 'active' : ''}`}
                                style={{ backgroundColor: 'transparent' }}
                            >
                                <GavelIcon className="me-3" />
                                {translate('admin.menu.legalDocuments')}
                            </button>
                        )}
                        {/* Hinweis: LMS-spezifische Verwaltung (Pflichtaufgaben, Lernpfade,
                            Lerngruppen, Peer-Reviews, Analytics, Marktplatz, lizenzierte
                            Kurse, Bestellungen, Landing-Pages, Vorlagen, Zahlungs-
                            Einstellungen) ist in die App (online-academy-frontend)
                            verschoben — der Admin bleibt auf SSO-/Mandanten-Verwaltung
                            (Firmen, Nutzer, Rollen/Rechte, Lizenzen) fokussiert. */}

                        {/* Language Switcher */}
                        <div className="position-relative">
                            <button
                                onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                                className="list-group-item list-group-item-action border-0 py-3 d-flex align-items-center justify-content-between text-white"
                                style={{ backgroundColor: 'transparent' }}
                            >
                                <div className="d-flex align-items-center">
                                    <TranslateIcon className="me-3" />
                                    {translate('admin.menu.language')}
                                </div>
                                <span>{currentLanguage?.flag} {currentLanguage?.name}</span>
                            </button>

                            {/* Language Dropdown */}
                            {languageMenuOpen && (
                                <div className="position-absolute w-100" style={{
                                    backgroundColor: navBgColor,
                                    zIndex: 1050,
                                    maxHeight: '300px',
                                    overflowY: 'auto'
                                }}>
                                    {SUPPORTED_LANGUAGES.map((language) => (
                                        <button
                                            key={language.code}
                                            onClick={() => handleLanguageChange(language.code)}
                                            className="list-group-item list-group-item-action border-0 py-2 d-flex align-items-center justify-content-between text-white"
                                            style={{
                                                backgroundColor: locale === language.code ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                                                fontSize: '20px'
                                            }}
                                        >
                                            <span>{language.flag} {language.name}</span>
                                            {locale === language.code && <span>✓</span>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Divider */}
                    <hr className="my-2" />

                    {/* Bottom Actions — Profil + Passwort-Aenderung leben seit
                        2026-09 ausschliesslich in der App (azubi:web-Feedback). */}
                    <div className="list-group list-group-flush">
                        <a
                            href={helpUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOffcanvasOpen(false)}
                            className="list-group-item list-group-item-action border-0 py-3 d-flex align-items-center text-white"
                            style={{ backgroundColor: 'transparent' }}
                        >
                            <HelpOutlineIcon className="me-3" />
                            {translate('admin.menu.help')}
                        </a>
                        <div className="px-3 py-3">
                            <button
                                onClick={() => {
                                    localStorage.removeItem('token');
                                    localStorage.removeItem('redirect_after_login');
                                    localStorage.removeItem('code_verifier');
                                    // Don't set user_logged_out flag - allows auto-login from SSO
                                    sessionStorage.clear();
                                    const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
                                    const dashboardUrl = `${ssoUrl}/dashboard`;
                                    window.location.href = dashboardUrl;
                                }}
                                className="btn btn-secondary"
                                style={{
                                    whiteSpace: 'nowrap',
                                    borderRadius: '290486px',
                                    paddingTop: '10.494px',
                                    paddingBottom: '10.494px',
                                    paddingLeft: '26.1px',
                                    paddingRight: '26.1px',
                                    width: '165px'
                                }}
                            >
                                <span>{translate('admin.menu.logout')}</span>
                                <i className="bi bi-box-arrow-right ms-2"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Backdrop */}
            {offcanvasOpen && (
                <div
                    className="offcanvas-backdrop fade show"
                    onClick={toggleOffcanvas}
                    style={{ zIndex: 1040 }}
                ></div>
            )}

            {/* ===== Bottom-Nav (Schnellzugriff, expandiert bei Hover) =====
                Markup + CSS identisch zum Frontend (design-system.css .bottomnav):
                die Item-Zeile zentriert sich über auto-margins der Randkinder und
                scrollt horizontal, wenn sie breiter als der Viewport ist. */}
            <nav className="bottomnav" style={{ background: navBgColor }}>
                <div className="items">
                    {bottomNavItems.map((item) => (
                        <button
                            key={item.to}
                            type="button"
                            className={`navitem${item.match(location.pathname) ? ' active' : ''}`}
                            onClick={() => handleNavigate(item.to)}
                        >
                            {item.icon}
                            <span className="label">{item.label}</span>
                        </button>
                    ))}
                </div>
                <span className="powered">powered by generation:l</span>
            </nav>

            {/* Language Loading Indicator */}
            <Backdrop
                open={loadingLanguage}
                sx={{
                    color: '#fff',
                    zIndex: 9999,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <CircularProgress color="inherit" size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        {translate('admin.menu.loadingTranslations')}
                    </Typography>
                </div>
            </Backdrop>
        </>
    );
};

// Title Bar Component to show page title above content
const TitleBar = () => {
    const location = useLocation();
    const translate = useTranslate();

    // Map routes to titles
    const getTitleForRoute = (pathname: string): string => {
        if (pathname === '/' || pathname === '/dashboard') return translate('admin.menu.dashboard');
        if (pathname.startsWith('/companies')) return translate('admin.menu.companies');
        if (pathname.startsWith('/users')) return translate('admin.menu.users');
        if (pathname.startsWith('/roles')) return translate('admin.menu.roles');
        if (pathname.startsWith('/permissions')) return translate('admin.menu.permissions');
        if (pathname.startsWith('/products')) return translate('admin.menu.products');
        if (pathname.startsWith('/licenses')) return translate('admin.menu.licenses');
        if (pathname.startsWith('/documents')) return translate('admin.menu.documents');
        if (pathname.startsWith('/legal-documents')) return translate('admin.menu.legalDocuments');
        if (pathname.startsWith('/organization-settings')) return translate('admin.menu.organizationSettings');
        return '';
    };

    const title = getTitleForRoute(location.pathname);

    if (!title) return null;

    return (
        <div style={{
            backgroundColor: '#fff'
        }}>
            {/* Größe/Abstände kommen aus design-system.css (.page-title) — als
                Inline-Style hätten sie die Mobile-Regel (1.75rem) ausgehebelt. */}
            <Typography variant="h1" component="h1" className="page-title">
                {title}
            </Typography>
        </div>
    );
};

// Main Layout Component
export const Layout = (props: any) => {
    return (
        <RaLayout
            {...props}
            appBar={CustomAppBar}
            menu={() => null} // Remove default sidebar
            sx={{
                '& .RaLayout-appFrame': {
                    marginTop: '64px', // Space for fixed navbar
                },
                '& .RaLayout-content': {
                    padding: 0,
                    marginLeft: '0 !important',
                },
                '& .RaSidebar-root': {
                    display: 'none',
                },
                '& .RaSidebar-docked': {
                    display: 'none',
                },
                '& .RaAppBar-menuButton': {
                    display: 'none',
                },
                // Inhaltsspalte wie im Frontend (.page in design-system.css) auf
                // 1480px begrenzen und zentrieren. Ohne die Begrenzung zog der
                // Inhalt bei 4K über die vollen 3840px — Kennzahlen-Kacheln wurden
                // ~2500px breit, Zahl links und Icon am gegenüberliegenden Rand.
                // Vertikales Padding bleibt unverändert, um den Abstands-Rhythmus
                // der bestehenden Seiten nicht zu verschieben.
                '& #main-content': {
                    boxSizing: 'border-box',
                    maxWidth: '1480px',
                    marginLeft: 'auto !important',
                    marginRight: 'auto !important',
                    // Schmale Viewports: 16px-Gutter wie im Frontend (.page), weniger
                    // Luft über dem Titel. Breakpoint-gebunden — nichts davon
                    // sticht in den Desktop durch.
                    paddingTop: { xs: '1.5rem !important', sm: '4.5rem !important' },
                    paddingBottom: '4.5rem !important',
                    paddingLeft: { xs: '16px !important', sm: '24px !important' },
                    paddingRight: { xs: '16px !important', sm: '24px !important' },
                },
            }}
        >
            <TitleBar />
            {/* Blockiert bei offenen Rechtsdokument-Zustimmungen (AGB etc.) */}
            <ConsentGate />
            {props.children}
        </RaLayout>
    );
};

export default Layout;
